// src/modulos/Parametros/BillingConditionPage.js
import React, { useState, useMemo, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import {
    collection,
    query,
    where,
    getDocs,
    limit
} from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const COLLECTION = 'parameters/data/billing-conditions';
const ITEMS_PER_PAGE = 10;

// Utility functions specific to this page
const validateDaysFormat = (daysString) => {
    if (!daysString || !daysString.trim()) return false;
    const regex = /^\d+(\/\d+)*$/;
    return regex.test(daysString.trim());
};

const formatDaysForDisplay = (daysString) => {
    if (!daysString) return '';
    return daysString.split('/').join(' / ');
};

const BillingConditionPage = ({ currentUser }) => {
    const can = usePagePermissions('condicao_de_faturamento');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [daysInput, setDaysInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [daysFormatError, setDaysFormatError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: conditions,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(COLLECTION, 'name');

    // Função para Verificar se Condição está em Uso
    const checkBillingConditionInUse = useCallback(async (conditionId) => {
        try {
            const salesQuery = query(
                collection(db, 'sales'),
                where('billingConditionId', '==', conditionId),
                limit(1)
            );
            const salesSnapshot = await getDocs(salesQuery);

            const invoicesQuery = query(
                collection(db, 'invoices'),
                where('billingConditionId', '==', conditionId),
                limit(1)
            );
            const invoicesSnapshot = await getDocs(invoicesQuery);

            const contractsQuery = query(
                collection(db, 'contracts'),
                where('billingConditionId', '==', conditionId),
                limit(1)
            );
            const contractsSnapshot = await getDocs(contractsQuery);

            return {
                inUse: !salesSnapshot.empty || !invoicesSnapshot.empty || !contractsSnapshot.empty,
                usedIn: {
                    sales: !salesSnapshot.empty,
                    invoices: !invoicesSnapshot.empty,
                    contracts: !contractsSnapshot.empty
                }
            };
        } catch (error) {
            console.error("Erro ao verificar uso da condição:", error);
            return { inUse: false, usedIn: {} };
        }
    }, []);

    // Reset e Modal
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setDaysInput('');
        setFormError('');
        setFormSuccess('');
        setDaysFormatError('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    // Funções CRUD
    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setDaysInput(item.days);
        setFormError('');
        setFormSuccess('');
        setDaysFormatError('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const { inUse, usedIn } = await checkBillingConditionInUse(id);

            if (inUse) {
                let message = `❌ Não é possível deletar "${name}" pois está sendo utilizado em:\n\n`;
                if (usedIn.sales) message += '• Vendas/Pedidos\n';
                if (usedIn.invoices) message += '• Faturas\n';
                if (usedIn.contracts) message += '• Contratos\n';
                message += '\n⚠️ Para manter a integridade do sistema, archive esta condição ou remova os vínculos primeiro.';
                alert(message);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                const totalItems = conditions?.length || 0;
                if (currentPage > 1 && (totalItems - 1) <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setFormSuccess(`Condição "${name}" deletada com sucesso!`);
                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Condição de Faturamento: " + result.error);
            }
        } catch (error) {
            console.error("Erro ao deletar:", error);
            setFormError("Erro ao deletar Condição de Faturamento. Tente novamente.");
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkBillingConditionInUse, deleteDocument, conditions?.length, currentPage]);

    // Submissão do Formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome da condição é obrigatório.");
            return;
        }

        if (!daysInput.trim()) {
            setFormError("A regra de dias é obrigatória.");
            return;
        }

        if (!validateDaysFormat(daysInput.trim())) {
            setFormError("Formato de dias inválido. Use números separados por '/' (ex: 30/60/90)");
            return;
        }

        // Verificar duplicidade
        const isDuplicate = conditions?.some(item =>
            item.name.toLowerCase() === nameInput.trim().toLowerCase() &&
            (!editingItem || item.id !== editingItem.id)
        );

        if (isDuplicate) {
            setFormError("Já existe uma condição com este nome. Por favor, use um nome diferente.");
            return;
        }

        const timestamps = getTimestamps(editingItem, currentUser?.email || 'system');
        const data = {
            name: nameInput.trim(),
            days: daysInput.trim(),
            ...timestamps
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Condição editada com sucesso!' : 'Condição adicionada com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            console.error("Erro ao salvar condição de faturamento:", error);
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, daysInput, editingItem, conditions, currentUser, addDocument, updateDocument, handleCloseModal]);

    // Filtragem e Paginação
    const filteredConditions = useMemo(() => {
        if (!conditions) return [];
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return conditions.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            item.days.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [conditions, searchTerm]);

    const totalFilteredItems = filteredConditions.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentConditions = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredConditions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredConditions, currentPage]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Form field change handlers
    const handleDaysChange = useCallback((e) => {
        const value = e.target.value;
        setDaysInput(value);
        if (value && !validateDaysFormat(value)) {
            setDaysFormatError('Formato inválido. Use números separados por "/"');
        } else {
            setDaysFormatError('');
        }
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Condição de Faturamento'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Salvar'}
            >
                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="name">
                            Nome da Condição <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="name"
                            type="text"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            className={styles.input}
                            placeholder="Ex: 30/60/90 Dias, À Vista, Parcelado"
                            required
                            disabled={formLoading}
                            autoFocus
                        />
                        <small className={styles.fieldHint}>Nome único e descritivo</small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="days">
                            Regra de Dias <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="days"
                            type="text"
                            value={daysInput}
                            onChange={handleDaysChange}
                            className={`${styles.input} ${daysFormatError ? styles.inputError : ''}`}
                            placeholder="Ex: 30/60/90"
                            required
                            disabled={formLoading}
                        />
                        {daysFormatError && (
                            <small className={styles.errorHint}>⚠️ {daysFormatError}</small>
                        )}
                        {daysInput && validateDaysFormat(daysInput) && (
                            <small className={styles.successHint}>✓ Formato válido: {formatDaysForDisplay(daysInput)}</small>
                        )}
                        <small className={styles.fieldHint}>
                            Digite os dias separados por "/" (ex: 30, 30/60, 30/60/90)
                        </small>
                    </div>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header - FIXED: Changed can.created to can.create */}
            <PageHeader
                title="Condições de Faturamento"
                actionText="Adicionar Condição"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome ou regra de dias..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
                {!listLoading && conditions && searchTerm && (
                    <div className={styles.searchInfo}>
                        {filteredConditions.length} resultado(s) encontrado(s)
                    </div>
                )}
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>❌ Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        🔄 Tentar novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
                    ) : filteredConditions.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={searchTerm ? 'Nenhuma condição encontrada' : 'Nenhuma condição cadastrada'}
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}". Tente outro termo de busca.`
                                    : 'Clique no botão "+ Adicionar Condição" para começar a cadastrar.'
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Condição de Faturamento</th>
                                            <th>Regra de Dias</th>
                                            <th className={styles.actionsHeader}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentConditions.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.createdAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Criado em: {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                    {item.updatedAt && item.updatedAt !== item.createdAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado em: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.daysBadge}>
                                                    <span className={styles.badge}>
                                                        {formatDaysForDisplay(item.days)}
                                                    </span>
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                            title="Editar condição"
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                            title="Deletar condição"
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="condições"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default BillingConditionPage;